<script setup lang="ts">
import { onActivated, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Filter, SearchQuery } from '../../types';
import { getSearchQueryFromRoute } from '../../query-formatters';
import { VTextField } from 'vuetify/components/VTextField';
import useLocale from '../../composables/useLocale';
import { useDisplay } from 'vuetify';

type Props = {
  filterDefinitions?: Filter;
  placeholder: string;
  focused?: boolean;
  enableSearchQuery?: boolean;
  rounded?: string;
  compact?: boolean;
};
type Emits = {
  // Without a source, the search takes it from the route
  (e: 'submit', query: SearchQuery, source?: string): void;
  (e: 'reset', source?: string): void;
};
const { t } = useLocale();
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const router = useRouter();
const route = useRoute();
const { smAndDown, platform, md, lg, xlAndUp } = useDisplay();
// Only holds the text of the input. The filters live in the route and are never written from here.
const getQueryFromRoute = (): SearchQuery => ({ q: getSearchQueryFromRoute(route.query).q ?? '' });
const searchQuery = ref<SearchQuery>(getQueryFromRoute());

const inputRef = ref<HTMLInputElement>();
const containerRef = ref<HTMLDivElement>();

// Only update q, so the filters and other params in the route are kept
const updateQueryParams = () => {
  const q = searchQuery.value.q || undefined;
  if (!props.enableSearchQuery || (getSearchQueryFromRoute(route.query).q || undefined) === q) {
    return;
  }
  router.push({ query: { ...route.query, q, source: 'search_box' } });
};

const handleReset = (source?: string) => {
  searchQuery.value.q = '';

  updateQueryParams();
  emit('reset', source);
};
const handleEscape = () => {
  handleReset('search_box');
  inputRef.value?.blur();
};

const handleSubmit = () => {
  updateQueryParams();
  triggerSearch('search_box');
};

const handleKeyPress = (event: KeyboardEvent) => {
  switch (event.keyCode) {
    case 13: // enter
      handleSubmit();
      return;
    case 27: // escape
      handleEscape();
      break;
    default:
    // ignore
  }
};
const triggerSearch = (source?: string) => {
  emit('submit', searchQuery.value, source);
};

const checkIfWeShouldSearch = () => {
  let shouldSearch = false;
  if (searchQuery.value.q && searchQuery.value.q!.length > 0) {
    shouldSearch = true;
  }

  // The search the page opens with: the user did not type it, so its source comes from the route
  if (shouldSearch) {
    triggerSearch();
  } else {
    handleReset();
  }
};

const setFocus = () => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

const initSearch = () => {
  /*
 We want to autofocus the search input but on mobile apps the keyboard takes
 too much space, so we only focus on web.
*/
  if (props.focused && !platform.value.touch) {
    setFocus();
  }

  checkIfWeShouldSearch();
};

//Avoid calling initSearch both in onMounted & onActivated when being part of keep-alive component.
const reInitOnActivated = ref(false);

// When the search input is part if a keep-alive component, we need to re-initialize it
onActivated(() => {
  if (reInitOnActivated.value) {
    // Resync from the route query so we don't auto-search on a stale `q` from before deactivation.
    searchQuery.value = getQueryFromRoute();
    initSearch();
  } else {
    reInitOnActivated.value = true;
  }
});

onMounted(() => {
  initSearch();
});

// Keeps the field in step when the page changes `q` itself, e.g. after a barcode scan.
watch(
  () => route.query.q,
  (q) => {
    if (props.enableSearchQuery) {
      searchQuery.value.q = (q as string | undefined) ?? '';
    }
  },
);

defineExpose({ clear: handleReset, focus: setFocus });
</script>

<template>
  <div ref="containerRef" class="search-input-container">
    <!-- @vue-ignore FIXME I don't know how to specify types for bindable attrs from top level-->
    <v-text-field
      id="search-input"
      ref="inputRef"
      v-model.trim="searchQuery.q"
      type="search"
      v-bind="{ ...$props, ...$attrs }"
      :placeholder="placeholder"
      :aria-label="t('content_search.label_search_bar')"
      variant="solo"
      :clearable="true"
      class="search-bar elevation-0"
      base-color="on-surface-low"
      color="on-surface"
      bg-color="surface-bright"
      active
      aria-describedby="search-input"
      @keydown="handleKeyPress"
      @click:clear="handleReset('search_box')"
    >
      <template v-if="smAndDown || compact" #prepend-inner>
        <v-icon>mdi-magnify</v-icon>
      </template>
      <template #label>
        <span class="d-none">{{ t('content_search.label_search_bar') }}</span>
      </template>
      <template #append-inner>
        <v-icon v-if="md && !compact" class="mr-4"></v-icon>
        <v-icon v-if="lg && !compact" class="mr-10"></v-icon>
        <v-icon v-if="xlAndUp && !compact" class="mr-16"></v-icon>
        <v-btn
          v-if="!compact"
          class="submit-btn d-none d-md-flex text-on-primary rounded-0"
          width="48"
          color="primary"
          variant="flat"
          icon
          :aria-label="t('content_search.search_button')"
          @click="handleSubmit"
        >
          <v-icon size="large">mdi-magnify</v-icon>
        </v-btn>
      </template>
    </v-text-field>
    <!-- Visible only md+ -->
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'vuetify/settings';

.search-input-container {
  width: 100%;
  position: relative;
}

:deep(.v-field),
:deep(.v-field__field) {
  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    padding-left: 8px;
  }
}
:deep(.v-field) {
  outline: 2px solid rgb(var(--v-theme-outline-variant));
  padding-right: 0;
  overflow: hidden;
}

:deep(.v-field--focused) {
  outline: 2px solid rgb(var(--v-theme-primary));
}
:deep(.v-field--variant-solo) {
  box-shadow: none;
}
.submit-btn {
  position: absolute;
  right: 0;
  top: 0;
}
.field {
  * > label {
    font-size: 2em;
  }
}
:deep(.v-text-field input) {
  padding: 9px 0 12px;
}
:deep(input) {
  outline: none !important;
}
.v-btn--icon.v-btn--density-default {
  height: 100% !important;
}
</style>
